import { FormControl } from '@angular/forms';
import { GenericFormUICDTO } from 'src/app/Common/UICServices/FormUIC/form-uic.interface';

/**
 * Automatically formats a landline number by inserting a dash after the STD code.
 * Accepts only FormControl as input.
 *
 * Example:
 *  Input: 02212345678 ➜ Output: 022-12345678
 *  Input: 04423456789 ➜ Output: 044-23456789
 */
export function formatPhoneNumber(control: FormControl): void {
  let value: string = control.value || '';

  // Clean up: remove spaces and dashes
  value = value.replace(/\s+/g, '').replace(/-/g, '');

  // Proceed only if it's a landline pattern
  if (value.startsWith('0') && value.length >= 11) {
    let stdCodeLength = 3;

    // Common 3-digit STD codes — tweak this as needed
    const known3DigitSTDCodes = ['011', '022', '033', '044', '080'];

    if (!known3DigitSTDCodes.includes(value.substring(0, 3))) {
      stdCodeLength = 4; // Assume 4-digit STD code if not found in 3-digit list
    }

    const std = value.substring(0, stdCodeLength);
    const number = value.substring(stdCodeLength);

    // Set the formatted value without triggering re-validation
    control.setValue(`${std}-${number}`, { emitEvent: false });
  }
}



/**
 * Converts a File object (e.g., from an <input type="file">) to a Base64-encoded string.
 * Returns a Promise that resolves with the Base64 string once the file is read.
 *
 * Example usage:
 *  toBase64(file).then(base64 => console.log(base64));
 *
 * @param file - The File object to be converted.
 * @returns A Promise that resolves with the Base64-encoded string.
 */
export function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}



export function lowercaseFirstLetter(str: string): string {
  if (!str) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
}


export function lowercaseFirstLetterKeys(obj: { [key: string]: any }): { [key: string]: any } {
  return Object.keys(obj).reduce((acc, key) => {
    const newKey = key.charAt(0).toLowerCase() + key.slice(1);
    acc[newKey] = obj[key];
    return acc;
  }, {} as Record<string, any>);
}



export function generateUICFormInterface(name: string, fields: GenericFormUICDTO[]): string {
  const body = fields.map(f => `  ${f.dbFeildName}: ${f.uiInputType};`).join('\n');
  return `interface ${name} {\n${body}\n}`;
}


export function formatDateForInput(date: string | Date): string {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function formatTimeToInput(timeString: string): string {
  if (!timeString) return '';
  return timeString.substring(0, 5); // Extracts "HH:mm"
}

export function formatTimeForReq(time: string): string | null {
  if (!time) return null;
  const [hh, mm] = time.split(':');
  return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00`; // convert to hh:mm:ss
};

export function convertAmountToWords(amount: number): string {
  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert_hundreds = (n: number): string => {
    let str = '';
    if (n > 99) {
      str += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n > 0) {
      if (n < 20) str += ones[n];
      else str += tens[Math.floor(n / 10)] + ' ' + ones[n % 10];
    }
    return str.trim();
  };

  const convert = (n: number): string => {
    if (n === 0) return 'Zero';
    let result = '';
    const billions = Math.floor(n / 1_000_000_000);
    const millions = Math.floor((n % 1_000_000_000) / 1_000_000);
    const thousands = Math.floor((n % 1_000_000) / 1000);
    const hundreds = n % 1000;

    if (billions) result += convert_hundreds(billions) + ' Billion ';
    if (millions) result += convert_hundreds(millions) + ' Million ';
    if (thousands) result += convert_hundreds(thousands) + ' Thousand ';
    if (hundreds) result += convert_hundreds(hundreds);

    return result.trim();
  };

  const dollars = Math.floor(amount);
  const cents = Math.round((amount - dollars) * 100);

  let words = convert(dollars) + ' Dollars';
  words += cents > 0 ? ` And ${convert(cents)} Cents` : ' And No Cents';

  return words;
}


export function propertiesChanged<T>(
  prev: T,
  curr: T,
  keysToCompare: (keyof T)[]
): boolean {
  // normalize keys to lowercase
  const lowerKeys = keysToCompare.map(k => k.toString().toLowerCase());

  return lowerKeys.some(lowerKey => {
    const prevVal = getValueIgnoreCase(prev, lowerKey);
    const currVal = getValueIgnoreCase(curr, lowerKey);
    console.log(prevVal,'===?',currVal);
    

    if (typeof prevVal === 'string' && typeof currVal === 'string') {
      return (prevVal ?? '').toLowerCase() !== (currVal ?? '').toLowerCase();
    }

    console.log();
    
    return (prevVal ?? null) !== (currVal ?? null);
  });
}

// helper to get property by case-insensitive key
function getValueIgnoreCase<T>(obj: T, lowerKey: string) {
  const actualKey = Object.keys(obj as any).find(
    k => k.toLowerCase() === lowerKey
  );
  return actualKey ? (obj as any)[actualKey] : undefined;
}

export function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /android|iphone|ipad|ipod|iemobile|blackberry|bada/i.test(userAgent.toLowerCase());
}


export function ensureString(value: unknown): string {
  return value !== null && value !== undefined ? String(value) : '';
}


// ...existing code...
export function ensureNumber(value: unknown): number {
  // Handle null, undefined, or empty string as 0
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  // If it's already a number, return it (handle NaN as 0)
  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value;
  }

  // If it's a string, trim and check if it's numeric
  if (typeof value === 'string') {
    const trimmed = value.trim();
    
    // Empty string after trim should return 0
    if (trimmed === '') {
      return 0;
    }

    const num = Number(trimmed);
    
    // If conversion results in NaN, it's not a valid number string
    if (isNaN(num)) {
      return 0;
    }
    
    return num;
  }

  // For any other type, throw an error
  throw new Error(`Invalid type: ${typeof value}. Expected number, string, null, or undefined`);
}
// ...existing code...